import { demoStudioNote, demoStudioStatus, heroSupporting, reps, type DemoStudioStatus } from '../content/reps';
import { CopyText, LeadText, Todo } from '../lib/marks';
import { isDev, siteEnv } from '../lib/siteEnv';
import s from '../site.module.css';
import { ICONS, IconBadge } from '../visuals/icons';
import { ChatExchange, DemoStudioIllustration } from '../visuals/Visuals';
import { CtaLink, CtaRow, Section, SiteLayout } from './layout';

/**
 * The HubSpot rep page at /reps. The rep sign-in itself lives at /reps/sign-in (features/reps/RepsPage.tsx). `status`
 * is only for tests: the page always renders with demoStudioStatus.
 */
export function RepsLandingPage({ status = demoStudioStatus }: { status?: DemoStudioStatus }) {
  const r = reps;
  return (
    <SiteLayout>
      <section className={s.hero} aria-labelledby="hero-title">
        <div className={`${s.container} ${s.heroGrid}`}>
          <div className={s.heroCopy}>
            <CopyText copy={r.hero.eyebrow} className={s.eyebrow} />
            <h1 id="hero-title">{r.hero.headline}</h1>
            <div className={s.stack}>
              <p className={s.lede}>{r.hero.subhead}</p>
              <CopyText copy={{ ...r.hero.supporting, text: heroSupporting(status) }} className={s.heroSupporting} />
            </div>
            <CtaRow primary={r.hero.primary} secondary={r.hero.secondary} onDark />
          </div>
          <DemoStudioIllustration visual={r.hero.visual} />
        </div>
      </section>

      <Section tone="white" labelledBy="stall-title">
        <div className={s.split}>
          <div className={s.stack}>
            <h2 id="stall-title">{r.stall.headline}</h2>
            <p className={s.body}>{r.stall.body}</p>
          </div>
          <ul className={s.checkList}>
            {r.stall.list.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      </Section>

      <Section labelledBy="get-title">
        <div className={s.sectionHead}>
          <h2 id="get-title">{r.whatYouGet.headline}</h2>
          <p className={s.lede}>{r.whatYouGet.body}</p>
        </div>
        <ul className={`${s.cards} ${s.cardsTwo}`}>
          {r.whatYouGet.cards.map((card, i) => (
            <li key={card.title} className={s.card}>
              <IconBadge icon={ICONS.whatYouGet[i]} />
              <h3>{card.title}</h3>
              <p>{card.body}</p>
              <p className={s.cardLink}>
                <a href={card.link.href}>{card.link.label}</a>
              </p>
            </li>
          ))}
        </ul>
      </Section>

      <Section tone="white" labelledBy="studio-title">
        <div className={s.sectionHead}>
          <h2 id="studio-title">{r.demoStudio.headline}</h2>
          <CopyText copy={r.demoStudio.body} className={s.lede} />
        </div>
        <ul className={`${s.cards} ${s.cardsTwo}`}>
          {r.demoStudio.cards.map((card, i) => (
            <li key={card.title} className={s.card}>
              <IconBadge icon={ICONS.demoStudio[i]} />
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </li>
          ))}
        </ul>
        <p className={s.closing}>{demoStudioNote(status)}</p>
      </Section>

      <Section labelledBy="technical-title">
        <div className={s.sectionHead}>
          <h2 id="technical-title">{r.technical.headline}</h2>
          <p className={s.lede}>{r.technical.body}</p>
        </div>
        <dl className={s.qaRows}>
          {r.technical.rows.map((row) => (
            <div key={row.question} className={s.qaRow}>
              <dt>{row.question}</dt>
              <dd>{row.answer}</dd>
            </div>
          ))}
        </dl>
      </Section>

      <Section tone="white" labelledBy="fit-title">
        <div className={s.split}>
          <div className={s.stack}>
            <h2 id="fit-title">{r.fitOnCall.headline}</h2>
            <p className={s.body}>{r.fitOnCall.body}</p>
            <p className={s.body}>{r.fitOnCall.waysIntro}</p>
            <ul className={s.plainList}>
              {r.fitOnCall.ways.map((w) => (
                <li key={w.lead}>
                  <LeadText item={w} />
                </li>
              ))}
            </ul>
          </div>
          <ChatExchange example={r.fitOnCall.example} />
        </div>
      </Section>

      <Section id={r.access.id} tone="sky" labelledBy="access-title">
        <div className={s.split}>
          <div className={s.stack}>
            <h2 id="access-title">{r.access.headline}</h2>
            <p className={s.body}>{r.access.body}</p>
            <div className={s.ctaRow}>
              <CtaLink cta={r.access.button} primary />
            </div>
          </div>
          <ol className={s.numbered}>
            {r.access.steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>
      </Section>

      <Section tone="navy" labelledBy="elsewhere-title">
        <div className={s.sectionHead}>
          <h2 id="elsewhere-title">{r.elsewhere.headline}</h2>
          <p className={s.lede}>{r.elsewhere.body}</p>
        </div>
        <ul className={s.plainList}>
          {r.elsewhere.list.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
        <p className={`${s.closing} ${s.onNavy}`}>{r.elsewhere.closing}</p>
      </Section>

      <Section tone="white" labelledBy="behind-title">
        <div className={s.sectionHead}>
          <h2 id="behind-title">{r.behind.headline}</h2>
          <p className={s.lede}>{r.behind.body}</p>
        </div>
      </Section>

      <Section tone="image" labelledBy="final-title">
        <div className={s.sectionHead}>
          <h2 id="final-title">{r.final.headline}</h2>
          <CtaRow primary={r.final.primary} secondary={r.final.secondary} onDark />
          <PartnerSmallPrint />
        </div>
      </Section>
    </SiteLayout>
  );
}

/** "Questions about partnering...? Email <address>." Without an address the line is left out of production builds. */
function PartnerSmallPrint() {
  const f = reps.final;
  if (siteEnv.partnerEmail) {
    return (
      <p className={s.small}>
        {f.smallPrint} <a href={`mailto:${siteEnv.partnerEmail}`}>{siteEnv.partnerEmail}</a>.
      </p>
    );
  }
  if (!isDev) return null;
  return (
    <p className={s.small}>
      {f.smallPrint} <Todo label={f.partnerTodo} />
    </p>
  );
}
